import { isAutomationActivityType } from '@flowsoft/observability/activity-taxonomy';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  CheckCircle,
  Edit3,
  Mail as MailIcon,
  MessageCircle,
  MessageSquare,
  Phone,
  PhoneOff,
  Plus,
  ShieldCheck,
  SkipForward,
  Sparkles,
  UserCheck,
} from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { Toggle } from '../../../components/primitives/Toggle';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import type { Activity } from '../../../types/leads';

const activityConfig: Record<
  string,
  { icon: ReactNode; color: string; bg: string; labelKey: string }
> = {
  created: {
    icon: <Plus size={14} color={colors.status.success.main} strokeWidth={2} />,
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    labelKey: 'leads.activityTimeline.created',
  },
  updated: {
    icon: <Edit3 size={14} color={colors.status.info.main} strokeWidth={2} />,
    color: colors.status.info.main,
    bg: colors.status.info.bg,
    labelKey: 'leads.activityTimeline.updated',
  },
  stage_change: {
    icon: <ArrowRight size={14} color={colors.accent.purple[500]} strokeWidth={2} />,
    color: colors.accent.purple[500],
    bg: withAlpha(colors.accent.purple[500], 0.08),
    labelKey: 'leads.activityTimeline.stageChanged',
  },
  note_added: {
    icon: <MessageSquare size={14} color={colors.primary[500]} strokeWidth={2} />,
    color: colors.primary[500],
    bg: colors.primary.muted,
    labelKey: 'leads.activityTimeline.noteAdded',
  },
  assigned: {
    icon: <UserCheck size={14} color={colors.status.warning.main} strokeWidth={2} />,
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    labelKey: 'leads.activityTimeline.assigned',
  },
  email_sent: {
    icon: <MailIcon size={14} color={colors.accent.blue[500]} strokeWidth={2} />,
    color: colors.accent.blue[500],
    bg: withAlpha(colors.accent.blue[500], 0.08),
    labelKey: 'leads.activityTimeline.emailSent',
  },
  title_exception: {
    icon: <ShieldCheck size={14} color={colors.status.warning.main} strokeWidth={2} />,
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    labelKey: 'leads.activityTimeline.titleException',
  },
  'agent.execution_started': {
    icon: <Bot size={14} color={colors.primary[500]} strokeWidth={2} />,
    color: colors.primary[500],
    bg: colors.primary.muted,
    labelKey: 'leads.activityTimeline.agentExecutionStarted',
  },
  'agent.execution_completed': {
    icon: <CheckCircle size={14} color={colors.status.success.main} strokeWidth={2} />,
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    labelKey: 'leads.activityTimeline.agentExecutionCompleted',
  },
  'agent.execution_failed': {
    icon: <AlertTriangle size={14} color={colors.status.error.main} strokeWidth={2} />,
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    labelKey: 'leads.activityTimeline.agentExecutionFailed',
  },
  'chat.tool.completed': {
    icon: <MessageCircle size={14} color={colors.accent.blue[500]} strokeWidth={2} />,
    color: colors.accent.blue[500],
    bg: withAlpha(colors.accent.blue[500], 0.08),
    labelKey: 'leads.activityTimeline.chatToolCompleted',
  },
  'voice.call.started': {
    icon: <Phone size={14} color={colors.status.success.main} strokeWidth={2} />,
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    labelKey: 'leads.activityTimeline.voiceCallStarted',
  },
  'voice.call.completed': {
    icon: <PhoneOff size={14} color={colors.text.secondary} strokeWidth={2} />,
    color: colors.text.secondary,
    bg: colors.background.secondary,
    labelKey: 'leads.activityTimeline.voiceCallCompleted',
  },
  'voice.call.credits_hold_failed': {
    icon: <PhoneOff size={14} color={colors.status.warning.main} strokeWidth={2} />,
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    labelKey: 'leads.activityTimeline.voiceCallSkippedNoCredits',
  },
  'automation.skipped': {
    icon: <SkipForward size={14} color={colors.text.disabled} strokeWidth={2} />,
    color: colors.text.disabled,
    bg: colors.background.secondary,
    labelKey: 'leads.activityTimeline.automationSkipped',
  },
  'enrichment.completed': {
    icon: <Sparkles size={14} color={colors.primary[500]} strokeWidth={2} />,
    color: colors.primary[500],
    bg: colors.primary.muted,
    labelKey: 'leads.activityTimeline.enrichmentCompleted',
  },
  // Types written by direct `prisma.activity.create` calls that predate the
  // dotted taxonomy. Historical rows are never migrated, so they still need a
  // real label instead of the generic "Activity" fallback.
  voice_call_status_changed: {
    icon: <Phone size={14} color={colors.text.secondary} strokeWidth={2} />,
    color: colors.text.secondary,
    bg: colors.background.secondary,
    labelKey: 'leads.activityTimeline.voiceCallStatusChanged',
  },
  'voice.call_analyzed': {
    icon: <Sparkles size={14} color={colors.accent.purple[500]} strokeWidth={2} />,
    color: colors.accent.purple[500],
    bg: withAlpha(colors.accent.purple[500], 0.08),
    labelKey: 'leads.activityTimeline.voiceCallAnalyzed',
  },
  voice_call_completed: {
    icon: <PhoneOff size={14} color={colors.text.secondary} strokeWidth={2} />,
    color: colors.text.secondary,
    bg: colors.background.secondary,
    labelKey: 'leads.activityTimeline.voiceCallCompleted',
  },
  voice_message_sent: {
    icon: <Phone size={14} color={colors.accent.blue[500]} strokeWidth={2} />,
    color: colors.accent.blue[500],
    bg: withAlpha(colors.accent.blue[500], 0.08),
    labelKey: 'leads.activityTimeline.voiceMessageSent',
  },
  auto_call_completed: {
    icon: <PhoneOff size={14} color={colors.text.secondary} strokeWidth={2} />,
    color: colors.text.secondary,
    bg: colors.background.secondary,
    labelKey: 'leads.activityTimeline.autoCallCompleted',
  },
  lead_enriched: {
    icon: <Sparkles size={14} color={colors.primary[500]} strokeWidth={2} />,
    color: colors.primary[500],
    bg: colors.primary.muted,
    labelKey: 'leads.activityTimeline.leadEnriched',
  },
};

const defaultConfig = {
  icon: <Edit3 size={14} color={colors.text.tertiary} strokeWidth={2} />,
  color: colors.text.tertiary,
  bg: colors.background.secondary,
  labelKey: 'leads.activityTimeline.activity',
};

const BACKEND_TEMPLATE_DESCRIPTIONS: Record<string, RegExp> = {
  note_added: /^AI agent \([^)]*\) added a note$/,
  created: /^Lead created \([^)]*\)$/,
};

function activityTitle(activity: Activity, labelKey: string, t: (key: string) => string): string {
  const template = BACKEND_TEMPLATE_DESCRIPTIONS[activity.activityType];
  if (!activity.description || template?.test(activity.description)) return t(labelKey);
  return activity.description;
}

function isAutomationActivity(activity: Activity): boolean {
  return isAutomationActivityType(activity.activityType, activity.metadata);
}

// FLOSEAI-560: activities written by the AI pipeline carry no real user and
// stamp their author in metadata.actor. Map that to the i18n label so a
// null-user row reads "by AI Agent" / "by System" instead of nothing.
function actorLabelKey(metadata: Record<string, unknown> | null | undefined): string | null {
  const actor = metadata?.actor;
  if (actor === 'ai_agent') return 'leads.activityTimeline.aiAgent';
  if (actor === 'system') return 'leads.activityTimeline.system';
  return null;
}

interface ActivityTimelineProps {
  activities: Activity[];
  /** Toggle state. Also drives client-side filtering unless `prefiltered`. */
  showAutomation?: boolean;
  onToggleAutomation?: (next: boolean) => void;
  /**
   * The caller already filtered — the paginated endpoint applies the same
   * taxonomy server-side. Filtering again here would hide the rows the toggle
   * just asked the server for, so this separates "already filtered" from
   * "toggle is on"; the two are not the same thing.
   */
  prefiltered?: boolean;
}

function AutomationToggle({
  showAutomation,
  onToggleAutomation,
  t,
}: {
  showAutomation: boolean;
  onToggleAutomation: (next: boolean) => void;
  t: (key: string) => string;
}) {
  return (
    <HStack spacing="sm" align="center" justify="end">
      <Text variant="caption" color={colors.text.tertiary}>
        {t('leads.activityTimeline.showAutomation')}
      </Text>
      <Toggle
        value={showAutomation}
        onValueChange={onToggleAutomation}
        size="sm"
        accessibilityLabel={t('leads.activityTimeline.showAutomation')}
      />
    </HStack>
  );
}

export function ActivityTimeline({
  activities,
  showAutomation = false,
  onToggleAutomation,
  prefiltered = false,
}: Readonly<ActivityTimelineProps>) {
  const { t, language } = useTranslation();

  const visible =
    prefiltered || showAutomation ? activities : activities.filter((a) => !isAutomationActivity(a));

  if (visible.length === 0) {
    return (
      <VStack spacing="sm">
        {onToggleAutomation !== undefined && (
          <AutomationToggle
            showAutomation={showAutomation}
            onToggleAutomation={onToggleAutomation}
            t={t}
          />
        )}
        <Box padding="lg" alignItems="center">
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leads.activityTimeline.noActivities')}
          </Text>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack spacing="none">
      {onToggleAutomation !== undefined && (
        <Box style={{ marginBottom: 12 }}>
          <AutomationToggle
            showAutomation={showAutomation}
            onToggleAutomation={onToggleAutomation}
            t={t}
          />
        </Box>
      )}

      {visible.map((activity, index) => {
        const config = activityConfig[activity.activityType] || defaultConfig;
        const isLast = index === visible.length - 1;
        const timeAgo = formatDistanceToNow(new Date(activity.createdAt), {
          addSuffix: true,
          locale: DATE_LOCALES[language],
        });
        const metadata = activity.metadata as Record<string, string> | null;
        const isViaChat = metadata?.origin === 'chat';

        return (
          <HStack key={activity.id} spacing="md" style={{ minHeight: 48 }}>
            <VStack align="center" style={{ width: 32 }}>
              {/* Tint, not ring — this dot sits inside a card, and a ringed
                  circle per activity turns the timeline into a chain of boxes. */}
              <Box
                width={32}
                height={32}
                borderRadius={16}
                backgroundColor={withAlpha(config.color, 0.16)}
                alignItems="center"
                justifyContent="center"
              >
                {config.icon}
              </Box>
              {!isLast && (
                <Box
                  width={1}
                  flex={1}
                  backgroundColor={colors.border.subtle}
                  style={{ minHeight: 16 }}
                />
              )}
            </VStack>

            <VStack spacing="xxs" style={{ flex: 1, paddingBottom: isLast ? 0 : 12 }}>
              <HStack spacing="xs" align="center" style={{ flexWrap: 'wrap' }}>
                <Text
                  variant="bodySmall"
                  color={colors.text.primary}
                  style={{ fontWeight: '600', flexShrink: 1 }}
                >
                  {activityTitle(activity, config.labelKey, t)}
                </Text>
                {isViaChat && (
                  <Badge color="cyan" variant="subtle" size="sm">
                    {t('leads.activityTimeline.viaChat')}
                  </Badge>
                )}
              </HStack>
              <Text
                variant="caption"
                color={colors.text.tertiary}
                style={{ fontSize: 11, fontFamily: darkPrecisionFontFamilies.metric }}
              >
                {timeAgo}
              </Text>

              {activity.user ? (
                <Text variant="caption" color={colors.text.tertiary}>
                  {t('leads.activityTimeline.by')} {activity.user.fullName}
                </Text>
              ) : (
                // FLOSEAI-560: AI/system-authored activities carry no real user.
                // Attribute them to the AI agent or the system via metadata.actor
                // instead of leaving the author blank or showing a person.
                actorLabelKey(metadata) && (
                  <Text variant="caption" color={colors.text.tertiary}>
                    {t('leads.activityTimeline.by')} {t(actorLabelKey(metadata) as string)}
                  </Text>
                )
              )}

              {activity.activityType === 'stage_change' && metadata?.oldStageName && (
                <HStack spacing="xs" align="center">
                  <Text variant="caption" color={colors.text.tertiary}>
                    {metadata.oldStageName}
                  </Text>
                  <ArrowRight size={10} color={colors.text.tertiary} strokeWidth={2} />
                  <Text variant="caption" color={config.color} style={{ fontWeight: '500' }}>
                    {metadata.newStageName}
                  </Text>
                </HStack>
              )}

              {activity.activityType === 'assigned' &&
                (metadata?.newAssignedName || metadata?.previousAssignedName) && (
                  <HStack spacing="xs" align="center">
                    {metadata.previousAssignedName ? (
                      <>
                        <Text variant="caption" color={colors.text.tertiary}>
                          {metadata.previousAssignedName}
                        </Text>
                        <ArrowRight size={10} color={colors.text.tertiary} strokeWidth={2} />
                      </>
                    ) : null}
                    <Text
                      variant="caption"
                      color={metadata.newAssignedName ? config.color : colors.text.tertiary}
                      style={{ fontWeight: '500' }}
                    >
                      {metadata.newAssignedName || t('leads.activityTimeline.unassigned')}
                    </Text>
                    {metadata.autoAssigned === 'true' && (
                      <Text variant="caption" color={colors.text.disabled} style={{ fontSize: 9 }}>
                        (auto)
                      </Text>
                    )}
                  </HStack>
                )}

              {(activity.activityType === 'title_exception' ||
                activity.activityType === 'automation.skipped' ||
                activity.activityType === 'stage_change') &&
                metadata?.reason && (
                  <Text
                    variant="caption"
                    color={colors.text.secondary}
                    style={{ fontStyle: 'italic' }}
                  >
                    {`"${metadata.reason}"`}
                  </Text>
                )}
            </VStack>
          </HStack>
        );
      })}
    </VStack>
  );
}
